import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { ArrowLeftIcon, CheckIcon, XMarkIcon, EyeIcon, ClockIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Pool {
  poolId: string
  name: string
  description: string
  type: 'football' | 'horse-racing'
  status: 'active' | 'completed' | 'cancelled'
  pickDeadline?: string
  games?: Game[]
  races?: Race[]
}

interface Race {
  raceNumber: number
  postTime: string
  track: string
  raceType: string
  conditions: string
  purse: string
  distance?: string
  entries: Horse[]
  scratches?: string[]
  status: 'upcoming' | 'running' | 'final'
  results?: {
    winner?: string
    place?: string
    show?: string
  }
}

interface Horse {
  horse_id: string
  post_position: string
  post_position_color?: {
    background_color: string
    text_color: string
    name: string
  }
  horse_name: string
  jockey: string
  trainer: string
  weight: string
  medication?: string
  age_sex: string
}

interface Game {
  gameId: string
  homeTeam: string
  awayTeam: string
  homeConference: string
  awayConference: string
  startDate: string
  week: number
  venue: string
  lines: Line[]
  mediaOutlets: string[]
  homeTeamRankings?: {
    ap?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
    coaches?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
  }
  awayTeamRankings?: {
    ap?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
    coaches?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
  }
  homeTeamRecord?: {
    team: string
    conference: string
    wins: number
    losses: number
    ties: number
    conferenceWins: number
    conferenceLosses: number
    conferenceTies: number
    homeWins: number
    homeLosses: number
    awayWins: number
    awayLosses: number
  } | null
  awayTeamRecord?: {
    team: string
    conference: string
    wins: number
    losses: number
    ties: number
    conferenceWins: number
    conferenceLosses: number
    conferenceTies: number
    homeWins: number
    homeLosses: number
    awayWins: number
    awayLosses: number
  } | null
}

interface Line {
  provider: string
  spread: number
  overUnder?: number
}

interface Pick {
  gameId?: string
  raceNumber?: number
  selectedTeam?: 'home' | 'away'
  confidence?: number
  horseId?: string
  horseName?: string
  postPosition?: string
  postPositionColor?: {
    background_color: string
    text_color: string
    name: string
  }
}

interface UserPicks {
  [key: string]: Pick // key can be gameId or raceNumber
}

export default function PoolPicksPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const { user, getAccessTokenSilently } = useAuth0()
  const [pool, setPool] = useState<Pool | null>(null)
  const [userPicks, setUserPicks] = useState<UserPicks>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [deadlinePassed, setDeadlinePassed] = useState(false)

  useEffect(() => {
    if (poolId) {
      fetchPoolAndPicks()
    }
  }, [poolId])

  const fetchPoolAndPicks = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      // Fetch pool details
      const poolResponse = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!poolResponse.ok) {
        throw new Error(`Failed to fetch pool: ${poolResponse.statusText}`)
      }

      const poolData = await poolResponse.json()
      
      // Handle different pool types
      if (poolData.pool.type === 'football' && poolData.pool.games) {
        // Ensure all gameIds are strings for consistent matching
        poolData.pool.games = poolData.pool.games.map((game: any) => ({
          ...game,
          gameId: game.gameId.toString()
        }))
      } else if (poolData.pool.type === 'horse-racing' && poolData.pool.races) {
        // Races are already in the correct format from the backend
        poolData.pool.races = poolData.pool.races
      }
      
      setPool(poolData.pool)

      // Check if deadline has passed
      if (poolData.pool.pickDeadline) {
        setDeadlinePassed(new Date() > new Date(poolData.pool.pickDeadline))
      }

      // Fetch user's existing picks
      try {
        const picksEndpoint = poolData.pool.type === 'horse-racing' 
          ? `${API_BASE_URL}/pools/${poolId}/horse-picks`
          : `${API_BASE_URL}/pools/${poolId}/picks`
          
        const picksResponse = await fetch(picksEndpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (picksResponse.ok) {
          const picksData = await picksResponse.json()
          const picksMap: UserPicks = {}
          
          if (poolData.pool.type === 'horse-racing') {
            // Handle horse racing picks
            picksData.picks?.forEach((pick: any) => {
              const raceKey = pick.raceNumber.toString()
              picksMap[raceKey] = {
                raceNumber: pick.raceNumber,
                horseId: pick.horseId,
                horseName: pick.horseName,
                postPosition: pick.postPosition,
                postPositionColor: pick.postPositionColor
              }
            })
          } else {
            // Handle football picks
            picksData.picks?.forEach((pick: any) => {
              const gameIdStr = pick.gameId.toString()
              picksMap[gameIdStr] = {
                gameId: gameIdStr,
                selectedTeam: pick.pick, // Map backend 'pick' field to frontend 'selectedTeam'
                confidence: pick.confidence
              }
            })
          }
          
          setUserPicks(picksMap)
        }
      } catch (picksError) {
        console.log('No existing picks found or error fetching picks')
      }

    } catch (err) {
      console.error('Error fetching pool and picks:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool data')
    } finally {
      setLoading(false)
    }
  }

  const handlePickChange = (gameId: string, selectedTeam: 'home' | 'away') => {
    setUserPicks(prev => ({
      ...prev,
      [gameId]: {
        gameId,
        selectedTeam
      }
    }))
    // Clear any success message when making changes
    if (successMessage) {
      setSuccessMessage(null)
    }
  }

  const handleHorsePickChange = (raceNumber: number, horse: Horse) => {
    setUserPicks(prev => ({
      ...prev,
      [raceNumber.toString()]: {
        raceNumber,
        horseId: horse.horse_id,
        horseName: horse.horse_name,
        postPosition: horse.post_position,
        postPositionColor: horse.post_position_color
      }
    }))
    // Clear any success message when making changes
    if (successMessage) {
      setSuccessMessage(null)
    }
  }

  const handleSavePicks = async () => {
    if (!poolId || deadlinePassed || !pool) return

    try {
      setSaving(true)
      setError(null)
      const token = await getAccessTokenSilently()

      if (pool.type === 'horse-racing') {
        // Handle horse racing picks
        const picks = Object.values(userPicks).map(pick => {
          const race = pool.races?.find(r => r.raceNumber === pick.raceNumber)
          const horse = race?.entries.find(h => h.horse_id === pick.horseId)
          return {
            raceNumber: pick.raceNumber,
            horseId: pick.horseId,
            horseName: pick.horseName,
            postPosition: pick.postPosition,
            postPositionColor: pick.postPositionColor,
            jockey: horse?.jockey || '',
            trainer: horse?.trainer || ''
          }
        })

        const response = await fetch(`${API_BASE_URL}/pools/${poolId}/horse-picks`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ picks })
        })

        if (!response.ok) {
          throw new Error(`Failed to save picks: ${response.statusText}`)
        }
      } else {
        // Handle football picks
        const picks = Object.values(userPicks).map(pick => ({
          gameId: pick.gameId,
          selectedTeam: pick.selectedTeam,
          confidence: pick.confidence
        }))

        const response = await fetch(`${API_BASE_URL}/pools/${poolId}/picks`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ picks })
        })

        if (!response.ok) {
          throw new Error(`Failed to save picks: ${response.statusText}`)
        }
      }

      setSuccessMessage('Picks saved successfully!')
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null)
      }, 3000)

    } catch (err) {
      console.error('Error saving picks:', err)
      setError(err instanceof Error ? err.message : 'Failed to save picks')
    } finally {
      setSaving(false)
    }
  }

  const formatGameTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getSpreadDisplay = (game: Game, team: 'home' | 'away') => {
    const dkLine = game.lines?.find(line => line.provider === 'DraftKings')
    const espnLine = game.lines?.find(line => line.provider === 'ESPN Bet')
    const spread = dkLine?.spread || espnLine?.spread

    if (spread === undefined || spread === null) return ''

    // Spread from API is always from HOME team perspective
    // For away team, we need to flip the sign  
    const teamSpread = team === 'home' ? spread : -spread
    const sign = teamSpread > 0 ? '+' : ''
    console.log(`Spread display for ${team}:`, { spread, teamSpread, sign })
    return ` (${sign}${teamSpread})`
  }

  const isGameStarted = (game: Game) => {
    return new Date(game.startDate) <= new Date()
  }

  const canMakePicks = pool?.status === 'active'
  const totalPicks = Object.keys(userPicks).length
  const totalGames = pool?.games?.length || 0
  const totalRaces = pool?.races?.length || 0
  const totalItems = pool?.type === 'horse-racing' ? totalRaces : totalGames

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  if (!pool) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pool not found</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link 
            to={`/admin/pools/football/${poolId}`}
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Make Your Picks</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{pool.name}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Picks Progress
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {totalPicks} of {totalItems} {pool?.type === 'horse-racing' ? 'races' : 'games'}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${totalItems > 0 ? (totalPicks / totalItems) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 mb-6">
          <Link
            to={`/pools/${poolId}/my-picks`}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <EyeIcon className="w-4 h-4 mr-2" />
            View My Picks
          </Link>
        </div>

        {/* Status Messages */}
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-md p-4">
            <div className="flex items-center">
              <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
              <div className="text-sm text-green-700 dark:text-green-400">{successMessage}</div>
            </div>
          </div>
        )}

        {/* Pick Deadline Warning */}
        {pool.pickDeadline && (
          <div className={`mb-6 border rounded-md p-4 ${
            deadlinePassed 
              ? 'bg-red-50 dark:bg-red-900/50 border-red-200 dark:border-red-800'
              : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
          }`}>
            <div className="flex items-center">
              <ClockIcon className={`w-5 h-5 mr-2 ${
                deadlinePassed ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'
              }`} />
              <div>
                <div className={`text-sm font-medium ${
                  deadlinePassed 
                    ? 'text-red-800 dark:text-red-200' 
                    : 'text-yellow-800 dark:text-yellow-200'
                }`}>
                  {deadlinePassed ? 'Pick Deadline Passed' : 'Pick Deadline'}
                </div>
                <div className={`text-sm ${
                  deadlinePassed 
                    ? 'text-red-700 dark:text-red-300' 
                    : 'text-yellow-700 dark:text-yellow-300'
                }`}>
                  {new Date(pool.pickDeadline).toLocaleString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                {deadlinePassed && (
                  <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                    ⚠️ Picks are now locked and cannot be changed
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!canMakePicks && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
            <div className="flex items-center">
              <XMarkIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
              <div className="text-sm text-yellow-700 dark:text-yellow-400">
                This pool is {pool.status}. Picks cannot be modified.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Games/Races */}
      <div className="space-y-6">
        {pool.type === 'horse-racing' && pool.races ? (
          // Horse Racing Picks
          pool.races.map((race) => {
            const userPick = userPicks[race.raceNumber.toString()]
            const isDisabled = !canMakePicks || deadlinePassed
            const availableHorses = race.entries.filter(horse => !race.scratches?.includes(horse.horse_id))

            return (
              <div 
                key={race.raceNumber} 
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6"
              >
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Race {race.raceNumber} - {race.raceType}
                  </h3>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {race.postTime} • {race.distance} • ${parseInt(race.purse).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {race.conditions}
                  </div>
                </div>

                {userPick && (
                  <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                    <div className="flex items-center">
                      <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-green-800 dark:text-green-200">
                          Your Pick: #{userPick.postPosition} {userPick.horseName}
                        </div>
                        {userPick.postPositionColor && (
                          <div className="flex items-center mt-1">
                            <div 
                              className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 mr-2"
                              style={{
                                backgroundColor: userPick.postPositionColor.background_color,
                              }}
                            />
                            <span className="text-xs text-green-700 dark:text-green-300">
                              {userPick.postPositionColor.name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {availableHorses.map((horse) => {
                    const isSelected = userPick?.horseId === horse.horse_id
                    const isScratched = race.scratches?.includes(horse.horse_id)
                    
                    return (
                      <button
                        key={horse.horse_id}
                        onClick={() => !isDisabled && !isScratched && handleHorsePickChange(race.raceNumber, horse)}
                        disabled={isDisabled || isScratched}
                        className={`p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                          isSelected
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : isScratched
                            ? 'border-red-300 bg-red-50 dark:bg-red-900/20 opacity-50'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        } ${isDisabled || isScratched ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center">
                            {horse.post_position_color ? (
                              <div 
                                className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-xs font-bold mr-3"
                                style={{
                                  backgroundColor: horse.post_position_color.background_color,
                                  color: horse.post_position_color.text_color
                                }}
                              >
                                {horse.post_position}
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold mr-3">
                                {horse.post_position}
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white text-sm">
                                {horse.horse_name}
                                {isScratched && (
                                  <span className="ml-2 text-xs text-red-600 dark:text-red-400">
                                    SCRATCHED
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                J: {horse.jockey}
                              </div>
                              <div className="text-xs text-gray-400 dark:text-gray-500">
                                T: {horse.trainer} • {horse.age_sex} • {horse.weight}lbs
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <CheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })
        ) : pool.games ? (
          // Football Picks
          pool.games.map((game) => {
          const gameStarted = isGameStarted(game)
          const userPick = userPicks[game.gameId]
          const isDisabled = !canMakePicks || gameStarted || deadlinePassed

          return (
            <div 
              key={game.gameId} 
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 ${
                gameStarted ? 'opacity-75' : ''
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    {formatGameTime(game.startDate)} • {game.venue}
                  </div>
                  {game.mediaOutlets.length > 0 && (
                    <div className="text-sm text-blue-600 dark:text-blue-400">
                      {game.mediaOutlets.join(', ')}
                    </div>
                  )}
                </div>
                {gameStarted && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                    Game Started
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Away Team */}
                <button
                  onClick={() => !isDisabled && handlePickChange(game.gameId, 'away')}
                  disabled={isDisabled}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    userPick?.selectedTeam === 'away'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      {game.awayTeamRankings?.ap?.rank && (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full mr-2">
                          {game.awayTeamRankings.ap.rank}
                        </span>
                      )}
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {game.awayTeam}{getSpreadDisplay(game, 'away')}
                        {game.awayTeamRecord && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                            ({game.awayTeamRecord.wins}-{game.awayTeamRecord.losses})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      @ {game.awayConference}
                    </div>
                    {userPick?.selectedTeam === 'away' && (
                      <CheckIcon className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mt-2" />
                    )}
                  </div>
                </button>

                {/* Home Team */}
                <button
                  onClick={() => !isDisabled && handlePickChange(game.gameId, 'home')}
                  disabled={isDisabled}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    userPick?.selectedTeam === 'home'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      {game.homeTeamRankings?.ap?.rank && (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full mr-2">
                          {game.homeTeamRankings.ap.rank}
                        </span>
                      )}
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {game.homeTeam}{getSpreadDisplay(game, 'home')}
                        {game.homeTeamRecord && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                            ({game.homeTeamRecord.wins}-{game.homeTeamRecord.losses})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {game.homeConference}
                    </div>
                    {userPick?.selectedTeam === 'home' && (
                      <CheckIcon className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mt-2" />
                    )}
                  </div>
                </button>
              </div>

              {/* Over/Under Info */}
              {game.lines.length > 0 && (
                <div className="mt-3 text-center text-sm text-gray-600 dark:text-gray-300">
                  {game.lines.map((line, index) => (
                    <span key={index} className="mr-4">
                      {line.provider}: {line.spread > 0 ? '+' : ''}{line.spread}
                      {line.overUnder && ` • O/U ${line.overUnder}`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )
        })) : (
          // No games or races
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              No {pool?.type === 'horse-racing' ? 'races' : 'games'} available for this pool.
            </p>
          </div>
        )}
      </div>

      {/* Save Button */}
      {canMakePicks && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleSavePicks}
            disabled={saving || totalPicks === 0 || deadlinePassed}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : `Save Picks (${totalPicks})`}
          </button>
        </div>
      )}
    </div>
  )
}
